import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SearchForm from "../shared/FrontHeader/SearchForm";
import { useGetAllCategoryListQuery } from "../../utils/api/category/category.api";
import { useGetProductListQuery } from "../../utils/api/product/product.api";
import type { Product } from "../../utils/api/product/product.type";
import { usePagination } from "../../utils/hooks";
import { ProductItemSkeleton } from "../HomePage/HomePage";
import ProduitCard from "./ProduitCard";
import Fade from "react-reveal/Fade";

const LIMIT = 20;

const ProduitsPage: React.FC = () => {
  const { page, setPage } = usePagination(LIMIT);
  const [searchParams, setSearchParams] = useSearchParams();
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const lastSyncedRef = useRef<{ page: number; count: number } | null>(null);

  const selectedCategorySlug = searchParams.get("categorie") || "";
  const q = searchParams.get("q") || searchParams.get("search") || "";

  const { data: categories, isLoading: isLoadingCategories } =
    useGetAllCategoryListQuery();

  const sortedCategories = useMemo(() => {
    if (!categories?.results?.length) return [];
    return [...categories.results].sort((a, b) =>
      (a?.nom ?? "").localeCompare(b?.nom ?? "", "fr", { sensitivity: "base" })
    );
  }, [categories?.results]);

  const { data = { results: [], count: 0 }, isFetching, isLoading } =
    useGetProductListQuery({
      page,
      limit: LIMIT,
      categorie: selectedCategorySlug || undefined,
      q: q || undefined,
    });

  useEffect(() => {
    if (!data?.results) return;

    const results = data.results;
    const count = data?.count ?? 0;
    setTotalCount(count);

    if (
      lastSyncedRef.current?.page === page &&
      lastSyncedRef.current?.count === count
    ) {
      return;
    }
    lastSyncedRef.current = { page, count };

    if (page === 1) {
      setAllProducts(results);
      setHasMore(results.length > 0 && results.length < count);
    } else {
      setAllProducts((prev) => {
        const existingIds = new Set(prev.map((p) => p?.id));
        const newItems = results.filter(
          (p) => p?.id != null && !existingIds.has(p.id)
        );
        if (!newItems.length) {
          setHasMore(false);
          return prev;
        }
        const next = [...prev, ...newItems];
        setHasMore(next.length < count);
        return next;
      });
    }
  }, [page, data]);

  const filtersKey = `${selectedCategorySlug}|${q}`;

  useEffect(() => {
    setPage(1);
    setAllProducts([]);
    lastSyncedRef.current = null;
    setHasMore(true);
  }, [filtersKey, setPage]);

  const loadMore = useCallback(() => {
    if (isFetching || !hasMore) return;
    setPage((p) => p + 1);
  }, [isFetching, hasMore, setPage]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { rootMargin: "200px", threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore]);

  const handleSelectCategory = (slug: string) => {
    const next = new URLSearchParams(searchParams);
    if (slug) {
      next.set("categorie", slug);
    } else {
      next.delete("categorie");
    }
    setSearchParams(next);
  };

  return (
    <div className="fb-produits-page">
      <section className="fb-produits-hero py-5">
        <div className="container text-center py-4">
          <Fade bottom>
            <p className="fb-eyebrow justify-content-center">
              BOUTIQUE NATURELLE
            </p>
            <h1 className="fb-title mb-3">Nos Produits</h1>
            <p className="fb-text mx-auto fb-produits-hero-text">
              Sélection rigoureuse de produits naturels, authentiques et
              traçables — au service de votre santé et de votre bien-être.
            </p>
          </Fade>
        </div>
      </section>

      <section className="fb-produits-toolbar">
        <div className="container">
          <div className="fb-produits-search mb-4">
            <SearchForm />
          </div>

          <div className="fb-produits-filters">
            <button
              type="button"
              className={`fb-filter-pill ${!selectedCategorySlug ? "active" : ""}`}
              onClick={() => handleSelectCategory("")}
            >
              Tous
            </button>

            {!isLoadingCategories &&
              sortedCategories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  className={`fb-filter-pill ${
                    selectedCategorySlug === cat.slug ? "active" : ""
                  }`}
                  onClick={() => handleSelectCategory(cat.slug)}
                >
                  {cat.nom}
                </button>
              ))}
          </div>
        </div>
      </section>

      <section className="fb-produits-grid-section pb-5">
        <div className="container">
          <p className="fb-produits-count mb-3">
            {totalCount || allProducts.length} produit
            {(totalCount || allProducts.length) > 1 ? "s" : ""}
          </p>

          <div className="fb-produits-grid">
            {page === 1 && (isLoading || isFetching) && !allProducts.length && (
              <ProductItemSkeleton />
            )}
            {allProducts.map((item) => (
              <ProduitCard key={item?.id} item={item} />
            ))}
          </div>

          <div
            ref={sentinelRef}
            className="infinite-scroll-sentinel"
            aria-hidden="true"
          />

          {isFetching && page > 1 && (
            <div className="d-flex justify-content-center py-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Chargement...</span>
              </div>
            </div>
          )}

          {!isLoading && !isFetching && allProducts.length === 0 && (
            <p className="fb-produits-empty text-center py-5">
              Aucun produit ne correspond à votre recherche.
            </p>
          )}
        </div>
      </section>
    </div>
  );
};

export default ProduitsPage;
